import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { VscSend, VscCloudDownload } from "react-icons/vsc";
import MessageBubble from "../shared/components/qa/MessageBubble";
import CampaignSidebar from "../shared/components/qa/CampaignSidebar";
import { BsListNested } from "react-icons/bs";
import { getCampaigns, getCampaignDetails, getCampaignSummaries, getAccountInfo, getSharedCampaigns, getSharedCampaignDetails } from "../../services/api";
import API_BASE_URL from "../../config";
import { useNavigate, useLocation, Link } from "react-router-dom";
import UpgradeButton from "../../components/UpgradeButton";

const QuestionAnswer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [message, setMessage] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDetails, setCampaignDetails] = useState({});
  const [sessionQA, setSessionQA] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [campaignSummaries, setCampaignSummaries] = useState({});
  const [hasStripeSubscription, setHasStripeSubscription] = useState(null); // null = loading, true = has subscription, false = no subscription
  const [hasValidAccess, setHasValidAccess] = useState(null); // null = loading, true = has access, false = no access
  const [activeTab, setActiveTab] = useState('myArchive');
  const [sharedCampaigns, setSharedCampaigns] = useState([]);
  const [sharedCampaignDetails, setSharedCampaignDetails] = useState({});

  // Add a ref to track if initial selection has been made
  const initialSelectionMade = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [sessionQA]);

  // Fetch account info to check access status
  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const accountInfo = await getAccountInfo();
        setHasStripeSubscription(accountInfo.hasStripeSubscription || false);
        setHasValidAccess(accountInfo.hasValidAccess || false);
      } catch (error) {
        console.error('Error fetching account info:', error);
        setHasStripeSubscription(false);
        setHasValidAccess(false);
      }
    };

    fetchAccountInfo();
  }, []);

  // Add effect to clear chat when session changes
  useEffect(() => {
    setSessionQA([]);
    setMessage("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [selectedSession]);

  // Add effect to clear chat when campaign changes
  useEffect(() => {
    setSessionQA([]);
    setMessage("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [selectedCampaign]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const transcriptId = params.get('transcript_id');
    const campaignId = params.get('campaign_id');
    
    console.log('QA page - URL parameters:', { transcriptId, campaignId });
    
    // Set initialSelectionMade to true if we have URL parameters
    // This prevents the CampaignSidebar from auto-selecting the first campaign
    if (transcriptId || campaignId) {
      console.log('QA page - Setting initialSelectionMade to true because URL parameters are present');
      initialSelectionMade.current = true;
    }
    
    if (transcriptId) {
      setSelectedSession(transcriptId);
      console.log('QA page - Selected session set from URL:', transcriptId);
    }
    
    if (campaignId) {
      console.log('QA page - Selected campaign set from URL:', campaignId);
      setSelectedCampaign(campaignId);
    }
  }, []);

  // Handle campaign selection after campaigns are loaded
  useEffect(() => {
    // Only proceed if campaigns are loaded
    if (campaigns.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const transcriptId = params.get('transcript_id');
    const campaignId = params.get('campaign_id');
    
    // Set initialSelectionMade to true if we have URL parameters
    // This prevents the CampaignSidebar from auto-selecting the first campaign
    if (transcriptId || campaignId) {
      console.log('QA page - Setting initialSelectionMade to true because URL parameters are present (after campaigns loaded)');
      initialSelectionMade.current = true;
    }
    
    if (campaignId) {
      console.log('QA page - Processing campaign_id after campaigns loaded:', campaignId);
      
      // Check if the campaign exists in our list
      // Convert both IDs to strings for comparison to ensure type consistency
      const campaignExists = campaigns.some(campaign => {
        if (!campaign || !campaign.id) return false;
        console.log('QA page - Comparing campaign ID:', campaign.id, 'Type:', typeof campaign.id);
        return String(campaign.id) === String(campaignId);
      });
      
      if (campaignExists) {
        // Find the matching campaign to get its ID with the correct type
        const matchingCampaign = campaigns.find(campaign => 
          campaign && campaign.id && String(campaign.id) === String(campaignId)
        );
        
        if (matchingCampaign) {
          // Use the ID directly from the campaigns array to ensure correct type
          setSelectedCampaign(matchingCampaign.id);
          console.log(`QA page - Setting selected campaign to ${matchingCampaign.id} (${typeof matchingCampaign.id}) from URL parameter`);
        } else {
          // Fallback to the URL parameter if for some reason we can't find the matching campaign
          setSelectedCampaign(campaignId);
          console.log(`QA page - Fallback: Setting selected campaign to ${campaignId} from URL parameter`);
        }
      } else {
        console.log(`QA page - Campaign ${campaignId} from URL parameter not found in available campaigns`);
      }
    }
    
    // Also handle transcript_id after campaigns are loaded
    if (transcriptId) {
      console.log('QA page - Processing transcript_id after campaigns loaded:', transcriptId);
      setSelectedSession(transcriptId);
    }
  }, [campaigns]);

  const showError = (message) => toast.error(message);
  const showLoading = () => toast.loading("Loading...");
  const dismissLoading = (toastId) => toast.dismiss(toastId);

  const fetchAllCampaignDetails = async (campaigns) => {
    const details = {};
    for (const campaign of campaigns) {
      try {
        const data = await getCampaignDetails(campaign.id);
        details[campaign.id] = data;
      } catch (err) {
        showError(`Failed to fetch details for campaign ${campaign.title}`);
      }
    }
    setCampaignDetails(details);
  };

  const refreshCampaigns = async () => {
    try {
      const data = await getCampaigns();
      if (data && data.campaigns) {
        setCampaigns(data.campaigns);
        await fetchAllCampaignDetails(data.campaigns);
      }
    } catch (error) {
      showError("Failed to refresh campaigns");
    }
  };

  const fetchSharedCampaigns = async () => {
    try {
      const data = await getSharedCampaigns();
      if (data && data.shared_campaigns) {
        setSharedCampaigns(data.shared_campaigns);
        await fetchAllSharedCampaignDetails(data.shared_campaigns);
      }
    } catch (error) {
      showError("Failed to fetch shared campaigns");
    }
  };

  const fetchAllSharedCampaignDetails = async (sharedCampaigns) => {
    const details = {};
    for (const campaign of sharedCampaigns) {
      try {
        if (!campaign || !campaign.campaign_id) continue;
        
        const data = await getSharedCampaignDetails(campaign.campaign_id);
        
        // Ensure the data has a transcripts array
        if (!data.transcripts) {
          data.transcripts = [];
        }
        
        details[campaign.campaign_id] = data;
      } catch (err) {
        showError(`Failed to fetch details for shared campaign ${campaign.campaign_name}`);
      }
    }
    console.log('All shared campaign details fetched:', details);
    setSharedCampaignDetails(details);
  };

  const handleSessionOperation = async (operation, ...args) => {
    const toastId = showLoading();
    try {
      await operation(...args);
      await refreshCampaigns();
    } catch (error) {
      showError(error.message);
    } finally {
      dismissLoading(toastId);
    }
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      const toastId = showLoading();
      try {
        const data = await getCampaigns();
        if (data && data.campaigns) {
          setCampaigns(data.campaigns);
          await fetchAllCampaignDetails(data.campaigns);
        }
      } catch (error) {
        showError("Failed to fetch campaigns");
      } finally {
        dismissLoading(toastId);
      }
    };

    const fetchSharedCampaignsData = async () => {
      try {
        await fetchSharedCampaigns();
      } catch (error) {
        console.error('Error fetching shared campaigns:', error);
        showError("Failed to fetch shared campaigns");
      }
    };

    fetchCampaigns();
    fetchSharedCampaignsData();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Clear selections when switching tabs
    setSelectedCampaign("");
    setSelectedSession(null);
    setSessionQA([]);
    setMessage("");
  };

  const handleInput = () => {
    const textarea = textareaRef.current;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    // Check if user has valid access to use the chatbot
    if (hasValidAccess === false) {
      toast.error("You need a valid subscription or plan to use the Q&A chatbot. Please upgrade your membership.");
      return;
    }
    
    // Check if we have either a selected session or a selected campaign with summaries
    if (!message.trim() || sendingMessage || 
        (!selectedSession && !(selectedCampaign && campaignSummaries[selectedCampaign]))) {
      return;
    }

    const messageToSend = message.trim();
    setMessage(""); // Clear message immediately
    textareaRef.current.style.height = "auto"; // Reset textarea height
    setSendingMessage(true);
    const toastId = showLoading();
    
    try {
      // Add user's message to the chat immediately
      setSessionQA(prev => [...prev, { content: messageToSend, type: "send" }]);
      
      let response;
      
      if (selectedSession) {
        // If a session is selected, use the existing transcript-based Q&A
        response = await fetch(`${API_BASE_URL}/api/ask_question/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            transcript_id: selectedSession,
            question: messageToSend
          }),
        });
      } else if (selectedCampaign && campaignSummaries[selectedCampaign]) {
        // If a campaign is selected (but no session), use the campaign's detailed summary
        const campaignSummary = campaignSummaries[selectedCampaign];
        const detailedSummary = campaignSummary.detailedSummary || '';
        
        // Use OpenAI API directly with the campaign summary as context
        response = await fetch(`${API_BASE_URL}/api/ask_question/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            campaign_id: selectedCampaign,
            question: messageToSend,
            context: detailedSummary
          }),
        });
      }

      if (!response || !response.ok) {
        throw new Error('Failed to get answer');
      }

      const data = await response.json();
      
      // Add AI's response to the chat
      setSessionQA(prev => [...prev, { content: data.answer, type: "received" }]);
    } catch (err) {
      showError(err.message);
      // Remove the user's message if there was an error
      setSessionQA(prev => prev.slice(0, -1));
    } finally {
      setSendingMessage(false);
      dismissLoading(toastId);
    }
  };

  const handleSessionSelect = (sessionId) => {
    setSelectedSession(sessionId);
    setSessionQA([]); // Clear previous Q&A when selecting a new session
    setIsListModalOpen(false);
    
    // Update URL with the selected session
    const params = new URLSearchParams(location.search);
    params.set('transcript_id', sessionId);
    if (selectedCampaign) {
      params.set('campaign_id', selectedCampaign);
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    console.log('QA page - Updated URL with session:', sessionId);
  };

  // Add a function to handle campaign selection that updates the URL
  const handleCampaignSelect = (campaignId) => {
    setSelectedCampaign(campaignId);
    setSelectedSession(null); // Clear selected session when changing campaigns
    
    // Update URL with the selected campaign
    const params = new URLSearchParams(location.search);
    params.set('campaign_id', campaignId);
    params.delete('transcript_id'); // Remove transcript_id when changing campaigns
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    console.log('QA page - Updated URL with campaign:', campaignId);
  };

  const handleDownloadChat = () => {
    const chatContent = sessionQA.map(msg => {
      const role = msg.type === "send" ? "User" : "AI";
      return `${role}: ${msg.content}\n`;
    }).join('\n');

    const blob = new Blob([chatContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chat-history.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchCampaignSummaries = async () => {
      if (!selectedCampaign) return;
      
      try {
        console.log('QA page - Fetching campaign summaries for campaign:', selectedCampaign);
        const data = await getCampaignSummaries(selectedCampaign);
        console.log('QA page - Fetched campaign summaries:', data);
        
        setCampaignSummaries(prev => ({
          ...prev,
          [selectedCampaign]: data
        }));
      } catch (err) {
        console.error('Error fetching campaign summaries:', err);
        // Only show error if it's not a 404 (campaign not found) or if it's a real error
        if (err.message.includes('404') || err.message.includes('not found')) {
          console.log('Campaign summaries not found, using empty summaries');
          setCampaignSummaries(prev => ({
            ...prev,
            [selectedCampaign]: {
              briefSummary: '',
              moderateSummary: '',
              detailedSummary: '',
              fullTranscript: ''
            }
          }));
        } else {
          showError(`Failed to fetch campaign summaries: ${err.message}`);
        }
      }
    };

    fetchCampaignSummaries();
  }, [selectedCampaign]);

  return (
    <>
      <button
        onClick={() => setIsListModalOpen(true)}
        className="bg-gray-100 mx-6 mt-1 sticky top-0 md:hidden text-sm mx-auto flex items-center justify-center bg-primary hover:bg-primary-light space-x-2 text-white rounded-lg p-1">
        <BsListNested size={20} />
        <span>View Campaigns</span>
      </button>

      <main className="min-h-[calc(100vh-88px)] md:h-[calc(100vh-56px)] md:flex flex-col md:flex-row gap-8 bg-gray-100 p-6 overflow-hidden">
        <CampaignSidebar
          origin="qa"
          campaigns={activeTab === 'myArchive' ? campaigns : sharedCampaigns}
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={handleCampaignSelect}
          campaignDetails={activeTab === 'myArchive' ? campaignDetails : sharedCampaignDetails}
          setSelectedSession={handleSessionSelect}
          selectedSession={selectedSession}
          isOpen={isListModalOpen}
          onClose={() => setIsListModalOpen(false)}
          handleSessionSelect={handleSessionSelect}
          onSessionOperation={handleSessionOperation}
          initialSelectionMade={initialSelectionMade}
          sharedCampaigns={sharedCampaigns}
          sharedCampaignDetails={sharedCampaignDetails}
          isSharedView={activeTab === 'sharedWithMe'}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        <div className="md:w-3/4 flex flex-col h-full overflow-hidden">
          <div className="max-w-4xl mx-auto w-full flex flex-col flex-grow overflow-hidden">
            <div className="bg-white border border-gray-300 rounded-lg p-6 mb-20 md:mb-4 flex-grow flex flex-col overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-black text-xl font-semibold flex-1">
                  {selectedSession 
                    ? "Ask Questions About This Session" 
                    : selectedCampaign && campaignSummaries[selectedCampaign]
                      ? "Ask Questions About This Campaign"
                      : "Select a Session or Campaign to Start Q&A"}
                </h2>
                {hasStripeSubscription === false && <UpgradeButton className="ml-4" />}
              </div>
              <p className="text-sm text-gray-500 mb-4 italic">
                Note: Chat history is not saved between sessions. Please download your chat if you'd like to keep it.
              </p>
              <div className="flex-grow overflow-y-auto scrollbar-thin space-y-4">
                {sessionQA.map((msg, index) => (
                  <MessageBubble
                    key={index}
                    message={msg}
                    type={msg.type}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="fixed bottom-0 left-0 w-full md:relative bg-white border border-gray-300 rounded-lg p-6 md:mb-4">
              {/* Access Warning Banner */}
              {hasValidAccess === false && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <p className="text-sm text-yellow-800">
                        Q&A chatbot requires a valid subscription or plan. Upgrade to start asking questions.
                      </p>
                    </div>
                    <UpgradeButton size="sm" />
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onInput={handleInput}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    selectedSession 
                      ? "Type your question about this session..." 
                      : selectedCampaign && campaignSummaries[selectedCampaign]
                        ? "Type your question about this campaign..."
                        : "Select a session or campaign to ask questions"
                  }
                  disabled={hasValidAccess === false || (!selectedSession && !(selectedCampaign && campaignSummaries[selectedCampaign]))}
                  className="flex-1 text-black outline-none px-2 py-2 rounded-lg resize-none overflow-y-auto scrollbar-thin w-full"
                  rows="1"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={hasValidAccess === false || !message.trim() || sendingMessage || (!selectedSession && !(selectedCampaign && campaignSummaries[selectedCampaign]))}
                  className={`px-4 py-2 rounded-full flex items-center gap-2 transition-colors
                ${
                  hasValidAccess !== false && message.trim() && !sendingMessage && (selectedSession || (selectedCampaign && campaignSummaries[selectedCampaign]))
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}>
                  Send <VscSend />
                </button>
              </div>
            </div>
            
            {/* Action buttons - moved outside the chat input box */}
            {(sessionQA.length > 0 || selectedSession || selectedCampaign) && (
              <div className="flex space-x-4 mt-4 mb-4">
                <button 
                  onClick={handleDownloadChat} 
                  className={`px-6 py-2 ${sessionQA.length > 0 ? 'bg-primary hover:bg-primary-light' : 'bg-gray-300 cursor-not-allowed'} text-white rounded-md flex-1 flex items-center justify-center gap-2`}
                  disabled={sessionQA.length === 0}>
                  <VscCloudDownload size={16} />
                  Download Chat
                </button>
                <Link
                  to={selectedSession 
                    ? `/archive?transcript_id=${String(selectedSession)}${selectedCampaign ? `&campaign_id=${String(selectedCampaign)}` : ''}` 
                    : selectedCampaign 
                      ? `/archive?campaign_id=${String(selectedCampaign)}` 
                      : '#'
                  }
                  className={`px-6 py-2 ${(selectedSession || selectedCampaign) ? 'bg-sky-900 hover:bg-sky-700' : 'bg-gray-300 cursor-not-allowed'} text-white rounded-md flex-1 text-center flex items-center justify-center gap-2`}
                  onClick={e => {
                    if (!selectedSession && !selectedCampaign) {
                      e.preventDefault();
                      return;
                    }
                    
                    // Log the navigation for debugging
                    console.log('Navigating to Archive with params:', { 
                      transcript_id: selectedSession ? String(selectedSession) : null, 
                      campaign_id: selectedCampaign ? String(selectedCampaign) : null 
                    });
                  }}>
                  View in Archive
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default QuestionAnswer;
